// Unpublished Work © 2025-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import EditLink from 'Ui/components/common/data-table/edit-link';
import FilterComponent from 'Ui/features/onequip/parts/inventory/filter-component';
import PartsCatalogLink from 'Ui/components/common/data-table/parts-catalog-link';
import {getPartsFromInventoryBins, getParts, deletePartBin} from 'Services/inventory-service';
import {getInventories} from 'Services/membership-service';
import {closeDialog, openDialog as openReduxDialog} from 'Store/actions/dialogs';
import {getHierarchy} from 'OnEquip/equipment/utils/membership-hierarchy-utils';
import {fetchEffectData} from 'Utils/react-utils';
import {getCurrencySymbol, formatNumber} from 'Utils/unit-conversion-utils';
import {CATEGORY, ACTIONS, LABELS} from 'Utils/analytics-utils';
import {getActionColumn} from 'Ui/features/onequip/parts/inventory/inventory-actions-column';
import dialogTypes from 'Ui/components/common/dialog-types';
import {THRESHOLD_CLASSNAMES} from 'Ui/constants/threshold-constants';
import {capitalizeFirstLetter, replaceTranslationNames} from 'Utils/translation-utils';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {
    VIEW_FINANCIAL_DATA,
    EDIT_PARTS,
    MANAGE_PARTS
} from 'Common/constants/business-activities';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {US} from 'Common/constants/pinpoint-constants';
import {setInventoryParts as setReduxInventoryParts} from 'Store/actions/equipment';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';

export function isShopDeereUrlEnabled(membership) {
    return membership?.country === US && membership?.isMigrated;
}

async function fetchInventoryParts(isMounted, setLoading, setPartsData, setMembershipHierarchy, superUser, appUserMembershipId, setInventoryParts) {
    setLoading(true);

    const [
        {inventories},
        hierarchy,
        {parts}
    ] = await Promise.all([
        getInventories(),
        getHierarchy(superUser, appUserMembershipId),
        getParts()
    ]);

    const availablePartsData = await getPartsFromInventoryBins(inventories);

    if (isMounted()) {
        const formattedAvailableParts = availablePartsData.map((part) => {
            const formattedQuantityOrdered = part.quantityOrdered ? formatNumber(part.quantityOrdered) : '-';
            const formattedQuantityReserved = part.quantityReserved ? formatNumber(part.quantityReserved) : '-';

            return {
                ...part,
                formattedInOutValue: `${formattedQuantityOrdered}/${formattedQuantityReserved}`,
                formattedTotalCost: formatNumber(part.totalCost, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                }),
                // Fallback for legacy part_bin rows where lastUnitCost may be null.
                lastUnitCost: part.lastUnitCost ?? part.unitCost,
                formattedLastUnitCost: formatNumber(part.lastUnitCost ?? part.unitCost, {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2
                }),
                formattedStock: formatNumber(part.quantity),
                stock: part.quantity
            };
        });

        const filteredParts = parts.filter((part) => Boolean(part.name));

        setPartsData({
            inventories,
            parts: filteredParts,
            formattedPartsFromInventoryBins: formattedAvailableParts
        });
        setInventoryParts(formattedAvailableParts);
        setMembershipHierarchy(hierarchy);
        setLoading(false);
    }
}

function getDataGridColumns(
    translations,
    membership,
    membershipHierarchy,
    partsData,
    openTransferInventoryDialog,
    openPullInventoryDialog,
    openStockInventoryDialog,
    dialogOnClose,
    openPartBinDialog,
    currencySymbol
) {
    return [
        {
            headerName: translations.TYPE,
            field: 'partType',
            minWidth: 250,
            flex: 1
        },
        {
            headerName: translations.NAME,
            field: 'name',
            minWidth: 250,
            renderCell: (params) => {
                return params.row &&
                    <EditLink
                        onClick={() => {
                            openPartBinDialog({
                                part: params.row,
                                translations,
                                updateInventoryData: dialogOnClose
                            });
                        }}
                    >
                        {params.row.name}
                    </EditLink>
                ;
            }
        },
        {
            headerName: translations.ONLINK_LOCATION,
            field: 'binLocation',
            minWidth: 200,
            flex: 1
        },
        {
            headerName: translations.MANUFACTURER,
            field: 'manufacturerName',
            minWidth: 150,
            flex: 1
        },
        {
            headerName: capitalizeFirstLetter(translations.PART_NUMBER),
            field: 'partNumber',
            minWidth: 150,
            renderCell: (params) => {
                return (
                    <PartsCatalogLink
                        analyticsEventData={{
                            category: CATEGORY.PARTS_CATALOG,
                            label: LABELS.PART_NUMBER,
                            action: ACTIONS.PARTS_INVENTORY,
                            value: params.row.partNumber
                        }}
                        manufacturerId={params.row.manufacturerId}
                        url={isShopDeereUrlEnabled(membership) ? `https://shop.deere.com/us/p/${params.row.partNumber}` :
                            `https://partscatalog.deere.com/jdrc/partdetails/partnum/${params.row.partNumber}`}
                        value={params.row.partNumber}
                    />
                );
            }
        },
        {
            headerName: `${translations.ONLINK_UNIT_COST} (${currencySymbol})`,
            field: 'lastUnitCost',
            type: 'number',
            minWidth: 150,
            flex: 1,
            renderCell: (params) => {
                return (
                    params.row.formattedLastUnitCost
                );
            },
            requiredMyJdPermissions: VIEW_FINANCIAL_DATA
        },
        {
            headerName: `${translations.VALUE} (${currencySymbol})`,
            field: 'totalCost',
            type: 'number',
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                return (
                    params.row.formattedTotalCost
                );
            },
            requiredMyJdPermissions: VIEW_FINANCIAL_DATA
        }, {
            headerName: translations.ONLINK_STOCK,
            field: 'quantity',
            type: 'number',
            width: 120,
            renderCell: (params) => {
                const part = params.row;

                let colorClass = '';

                if (part.thresholdLevel) {
                    if (part.quantity === 0) {
                        colorClass = THRESHOLD_CLASSNAMES.extremeThresholdText;
                    } else if (part.quantity < part.thresholdLevel) {
                        colorClass = THRESHOLD_CLASSNAMES.warningThresholdText;
                    } else {
                        colorClass = THRESHOLD_CLASSNAMES.normalThresholdText;
                    }
                }

                return (
                    <span className={colorClass}>{part.formattedStock}</span>
                );
            }
        },
        {
            headerName: `${translations.ONLINK_IN}/${translations.ONLINK_OUT}`,
            field: 'formattedInOutValue',
            type: 'number',
            sortable: false,
            width: 120
        },
        {
            headerName: '',
            field: 'actions',
            sortable: false,
            width: 100,
            renderCell: (params) => {
                return getActionColumn(
                    params.row,
                    openTransferInventoryDialog,
                    openPullInventoryDialog,
                    openStockInventoryDialog,
                    membershipHierarchy,
                    partsData,
                    dialogOnClose
                );
            },
            requiredMyJdPermissions: EDIT_PARTS
        }
    ].filter(Boolean);
}

function Inventory(props) {
    const {
        closeConfirmation,
        openConfirmation,
        appUserMembershipId,
        membership,
        myJdPermissions,
        openPullInventoryDialog,
        openStockInventoryDialog,
        openTransferInventoryDialog,
        openPartBinDialog,
        superUser,
        translations,
        setInventoryParts
    } = props;

    const {addToast} = useToast();
    const [partsData, setPartsData] = React.useState({
        inventories: [],
        parts: [],
        formattedPartsFromInventoryBins: []
    });
    const [loading, setLoading] = React.useState(true);
    const [membershipHierarchy, setMembershipHierarchy] = React.useState({});

    const currencySymbol = getCurrencySymbol({
        currencyPreference: membership.currencyPreference
    });

    React.useEffect(() => fetchEffectData(async (isMounted) =>
        fetchInventoryParts(
            isMounted,
            setLoading,
            setPartsData,
            setMembershipHierarchy,
            superUser,
            appUserMembershipId,
            setInventoryParts
        )),
    [membership.membershipId]
    );

    const dialogOnClose = () => fetchInventoryParts(
        () => true,
        setLoading,
        setPartsData,
        setMembershipHierarchy,
        superUser,
        appUserMembershipId,
        setInventoryParts
    );

    function deletePartBinHandler(partBin) {
        openConfirmation({
            message: replaceTranslationNames(translations.ONLINK_ITEM_THIS_CANNOT_BE_UNDONE, {
                '0': translations.ONLINK_PART_BIN?.toLowerCase()
            }),
            title: replaceTranslationNames(translations.ONLINK_DELETE_ITEM, {
                '0': translations.ONLINK_PART_BIN
            }),
            async onContinue() {
                closeConfirmation();

                try {
                    await deletePartBin(partBin.partBinId);

                    await dialogOnClose();
                } catch {
                    addToast({
                        message: replaceTranslationNames(translations.ONLINK_CONFIRM_ITEM_DELETION_FAILED, {
                            '0': translations.ONLINK_PART_BIN?.toLowerCase()
                        }),
                        type: TOAST_TYPE.ERROR
                    });
                }
            },
            onCancel: closeConfirmation
        });
    }

    const userAuth = {
        myJdPermissions,
        isMigrated: membership.isMigrated
    };

    const filterComponent = (<FilterComponent
        addToast={addToast}
        country={membership.country}
        currencySymbol={currencySymbol}
        dialogOnClose={dialogOnClose}
        includeUnitCostColumn={true}
        isMigrated={membership.isMigrated}
        loading={loading}
        membershipHierarchy={membershipHierarchy}
        openPullInventoryDialog={openPullInventoryDialog}
        openStockInventoryDialog={openStockInventoryDialog}
        openTransferInventoryDialog={openTransferInventoryDialog}
        partsData={partsData}
        translations={translations}
    />);

    return (
        <DataGridWrapper
            columns={getDataGridColumns(
                translations,
                membership,
                membershipHierarchy,
                partsData,
                openTransferInventoryDialog,
                openPullInventoryDialog,
                openStockInventoryDialog,
                dialogOnClose,
                openPartBinDialog,
                currencySymbol
            )}
            deleteFilter={(partBin) => partBin.quantity <= 0}
            deleteHandler={deletePartBinHandler}
            filterComponent={filterComponent}
            getRowId={(row) => row.partId}
            initialState={{
                sorting: {
                    sortModel: [{
                        field: 'partType',
                        sort: 'asc'
                    }, {
                        field: 'name',
                        sort: 'asc'
                    }]
                }
            }}
            loading={loading}
            rows={partsData.formattedPartsFromInventoryBins}
            searchable={true}
            showDeleteColumn={isAuthorized({
                myJdPermissions: MANAGE_PARTS
            }, userAuth)}
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            translations={translations}
        />
    );
}

Inventory.propTypes = {
    appUserMembershipId: PropTypes.string,
    closeConfirmation: PropTypes.func,
    currencyPreference: PropTypes.string,
    membership: PropTypes.membership,
    myJdPermissions: PropTypes.myJdPermissions,
    openConfirmation: PropTypes.func,
    openPartBinDialog: PropTypes.func,
    openPullInventoryDialog: PropTypes.func,
    openStockInventoryDialog: PropTypes.func,
    openTransferInventoryDialog: PropTypes.func,
    setInventoryParts: PropTypes.func,
    superUser: PropTypes.bool,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        appUserMembershipId: state.account.membershipId,
        membership: state.membership,
        myJdPermissions: state.account.myJdPermissions,
        superUser: state.account.superUser,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        openPartBinDialog(props) {
            dispatch(openReduxDialog(dialogTypes.PART_BIN_DIALOG, props));
        },
        openTransferInventoryDialog(props) {
            dispatch(openReduxDialog(dialogTypes.TRANSFER_INVENTORY_DIALOG, props));
        },
        openPullInventoryDialog(props) {
            dispatch(openReduxDialog(dialogTypes.PULL_INVENTORY_DIALOG, props));
        },
        openStockInventoryDialog(props) {
            dispatch(openReduxDialog(dialogTypes.STOCK_INVENTORY_DIALOG, props));
        },
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openReduxDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        },
        setInventoryParts(value) {
            dispatch(setReduxInventoryParts(value));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(Inventory);
